import { useCallback, useEffect, useState } from 'react'
import { ErroreApi } from '../servizi/api.ts'
import { Attesa, Avviso } from '../componenti/Pezzi.tsx'
import { Bottone, Scelta } from '../componenti/base.tsx'
import * as posta from '../servizi/posta.ts'

/**
 * Amministrazione > Utenti > Modifica: «Caselle di gruppo» (483).
 *
 * Le caselle condivise che la persona usa in Posta oltre alla propria, con «Aggiungi» e «Togli».
 * Lo usa chi gestisce le utenze (`utente.gestisci`), che e' anche chi crea le caselle in Posta →
 * «Caselle di gruppo». Aggiungere e togliere valgono subito, fuori dal «Salva» della finestra: la
 * persona trova o perde la casella alla prossima pagina che apre, e ogni passo resta a registro.
 *
 * Per un collaboratore esterno (533) sono le sue «Caselle di servizio»: caselle di gruppo che usa, anche
 * con altre persone (narni@, crosia@...), piu' la cidisonlus.org in deroga.
 */

const testo = (e: unknown) =>
  (e instanceof ErroreApi ? e.message : 'Non è andata a buon fine: controlla la connessione e riprova.')

export function CaselleDiGruppoUtente({ utenteId, servizio = false }: {
  utenteId: string
  /** Collaboratore esterno (533): le caselle di gruppo sono le sue caselle di servizio. */
  servizio?: boolean
}) {
  const [caselle, setCaselle] = useState<posta.CasellaGruppo[] | null>(null)
  const [scelta, setScelta] = useState('')
  // Livello (504): «legge e scrive» e' l'accesso pieno di sempre; «legge» apre e basta.
  const [livello, setLivello] = useState<posta.LivelloCasella>('scrive')
  const [errore, setErrore] = useState<string | null>(null)
  const [messaggio, setMessaggio] = useState<string | null>(null)
  const [inCorso, setInCorso] = useState<string | null>(null)

  const carica = useCallback(() => {
    posta.elencaGruppi().then(setCaselle).catch((e) => setErrore(testo(e)))
  }, [])
  useEffect(carica, [carica])

  const sue = (caselle ?? []).filter((c) => c.membri.some((m) => m.id === utenteId))
  const altre = (caselle ?? []).filter((c) => !c.membri.some((m) => m.id === utenteId))

  async function aggiungi() {
    const casella = altre.find((c) => c.id === scelta)
    if (!casella) return
    setInCorso('aggiungi'); setErrore(null); setMessaggio(null)
    try {
      await posta.aggiungiMembro(casella.id, utenteId, livello)
      setMessaggio(`Aggiunta ${casella.indirizzo} (${posta.ETICHETTE_LIVELLO[livello]}).`)
      setScelta('')
      carica()
    } catch (e) { setErrore(testo(e)) }
    finally { setInCorso(null) }
  }

  async function togli(casella: posta.CasellaGruppo) {
    setInCorso(casella.id); setErrore(null); setMessaggio(null)
    try {
      await posta.togliMembro(casella.id, utenteId)
      setMessaggio(`Tolta ${casella.indirizzo}.`)
      carica()
    } catch (e) { setErrore(testo(e)) }
    finally { setInCorso(null) }
  }

  return (
    <section className="operatori-sezione">
      <h3 className="operatori-titoletto">{servizio ? 'Caselle di servizio' : 'Caselle di gruppo'}</h3>
      <p className="piccolo tenue">
        {servizio
          ? 'Le caselle di servizio del collaboratore: caselle condivise che usa in Posta oltre alla sua, anche '
            + 'insieme ad altre persone. Spediscono e ricevono come ogni casella CIDIS. '
          : 'Le caselle condivise che la persona usa in Posta, oltre alla sua, con il livello: «legge e '
            + 'scrive» oppure «legge». '}
        «Aggiungi» e «Togli» valgono subito, senza «Salva». Le caselle di progetto si danno dalla pagina del
        progetto.
      </p>
      {errore && <Avviso tipo="errore">{errore}</Avviso>}
      {messaggio && <Avviso tipo="successo">{messaggio}</Avviso>}
      {!caselle ? (!errore && <Attesa testo="Caricamento…" />) : (
        <>
          {caselle.length === 0 && (
            <p className="piccolo tenue">
              Non ci sono ancora caselle di gruppo: si creano in Posta → «Caselle condivise».
            </p>
          )}
          {sue.length > 0 && (
            <ul className="operatori-variazioni">
              {sue.map((c) => (
                <li key={c.id}>
                  <div className="riga">
                    <span>
                      {c.nome_visualizzato} · <span className="mono">{c.indirizzo}</span>
                      {c.membri.find((m) => m.id === utenteId)?.livello === 'legge'
                        && <span className="tenue"> · sola lettura</span>}
                      {!c.attiva && <span className="tenue"> · disattivata</span>}
                    </span>
                    <Bottone variante="discreto" dimensione="piccolo" inCorso={inCorso === c.id}
                             aria-label={`Togli ${c.indirizzo}`} onClick={() => void togli(c)}>
                      Togli
                    </Bottone>
                  </div>
                </li>
              ))}
            </ul>
          )}
          {caselle.length > 0 && sue.length === 0 && (
            <p className="piccolo tenue">{servizio ? 'Nessuna casella di servizio.' : 'Nessuna casella di gruppo.'}</p>
          )}
          {altre.length > 0 && (
            <div className="riga">
              <Scelta etichetta="Aggiungi una casella" vuota="Scegli…" valore={scelta} cambia={setScelta}
                      opzioni={altre.map((c) => ({
                        valore: c.id,
                        etichetta: `${c.nome_visualizzato} <${c.indirizzo}>${c.attiva ? '' : ' (disattivata)'}`,
                      }))} />
              <Scelta etichetta="Livello" valore={livello} cambia={(v) => setLivello(v as posta.LivelloCasella)}
                      opzioni={[{ valore: 'scrive', etichetta: 'legge e scrive' }, { valore: 'legge', etichetta: 'legge' }]} />
              <Bottone disabled={!scelta} inCorso={inCorso === 'aggiungi'} onClick={() => void aggiungi()}>
                Aggiungi
              </Bottone>
            </div>
          )}
        </>
      )}
    </section>
  )
}
